import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  getDayOfYear,
  getDaysInYear,
  isWeekend,
  parseISO,
  startOfISOWeek,
} from 'date-fns';

export type UsageKind = 'pto' | 'sick' | 'wfh';

export interface UsageBucket {
  used: number;
  scheduled: number;
  pending: number;
}

export interface TimeOffUsageSummary {
  pto: UsageBucket;
  sick: UsageBucket;
  wfh: UsageBucket;
  combined: UsageBucket;
}

export interface TimeOffUsageEntry {
  _id?: string;
  id?: string;
  employee?: string | { _id?: string; id?: string };
  startDate: string;
  endDate?: string;
  type: string;
  status: string;
  hours: number;
  outlookEventId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type PaceStatus = 'within-pace' | 'over-pace' | 'pending-risk' | 'over-allotment' | 'no-allotment';

export interface AllocationPace {
  allotment: number;
  used: number;
  scheduled: number;
  pending: number;
  committed: number;
  potential: number;
  available: number;
  projected: number;
  usedPercent: number;
  scheduledPercent: number;
  pendingPercent: number;
  elapsedPercent: number;
  status: PaceStatus;
}

export interface YearProgress {
  year: number;
  dayOfYear: number;
  daysInYear: number;
  elapsedFraction: number;
  percentComplete: number;
}

export interface WfhWeeklyPlan {
  weeklyAllowance: number;
  weeksWithUsage: number;
  weeksOverAllowance: number;
  highestWeekDays: number;
}

const EMPTY_BUCKET = (): UsageBucket => ({ used: 0, scheduled: 0, pending: 0 });

const toDateKey = (value?: string) => (value || '').slice(0, 10);

const isValidDateKey = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = parseISO(value);
  return !Number.isNaN(parsed.getTime()) && format(parsed, 'yyyy-MM-dd') === value;
};

// Timestamps arrive as strings whose offset and precision depend on the source,
// so comparing them as text can order two rows by their local wall clock.
const toTimestamp = (value?: string) => {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 0;
};

const round = (value: number, precision = 2) => {
  const factor = 10 ** precision;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

// Mirrors backend/src/utils/dateUtils.ts exactly. Calendar-imported hours are
// calculated without these company holidays, so the UI must use the same work
// days when splitting a multi-day entry into elapsed and scheduled portions.
const MONDAY = 1;
const THURSDAY = 4;

// The nth (1-based) occurrence of a weekday in a month, e.g. 3rd Monday of January.
const nthWeekdayOfMonth = (year: number, month: number, weekday: number, nth: number) => {
  const firstOfMonth = new Date(year, month, 1);
  const offset = (weekday - firstOfMonth.getDay() + 7) % 7;
  return new Date(year, month, 1 + offset + (nth - 1) * 7);
};

// The last occurrence of a weekday in a month, e.g. last Monday of May.
const lastWeekdayOfMonth = (year: number, month: number, weekday: number) => {
  const lastOfMonth = new Date(year, month + 1, 0);
  const offset = (lastOfMonth.getDay() - weekday + 7) % 7;
  return new Date(year, month, lastOfMonth.getDate() - offset);
};

// Fixed-date holidays observe Friday when they fall on Saturday and Monday
// when they fall on Sunday; floating nth-weekday holidays never need a shift.
const getObservedHoliday = (date: Date) => {
  if (date.getDay() === 6) return addDays(date, -1);
  if (date.getDay() === 0) return addDays(date, 1);
  return date;
};

const getSbgHolidays = (year: number) => [
  getObservedHoliday(new Date(year, 0, 1)),   // New Year's Day
  nthWeekdayOfMonth(year, 0, MONDAY, 3),      // Martin Luther King Jr. Day
  nthWeekdayOfMonth(year, 1, MONDAY, 3),      // Presidents' Day
  lastWeekdayOfMonth(year, 4, MONDAY),        // Memorial Day
  getObservedHoliday(new Date(year, 5, 19)),  // Juneteenth
  getObservedHoliday(new Date(year, 6, 4)),   // Independence Day
  nthWeekdayOfMonth(year, 8, MONDAY, 1),      // Labor Day
  nthWeekdayOfMonth(year, 9, MONDAY, 2),      // Columbus Day
  getObservedHoliday(new Date(year, 10, 11)), // Veterans Day
  nthWeekdayOfMonth(year, 10, THURSDAY, 4),   // Thanksgiving
  getObservedHoliday(new Date(year, 11, 25)), // Christmas Day
];

// Next year's observed New Year's Day can land on Dec 31 of the current year
// (whenever Jan 1 falls on a Saturday), so both year lists must be checked.
// Cached per year: business-day math calls this once for every day in a range.
const sbgHolidayKeysCache = new Map<number, Set<string>>();
const getSbgHolidayKeysNear = (year: number) => {
  let keys = sbgHolidayKeysCache.get(year);
  if (!keys) {
    keys = new Set(
      [...getSbgHolidays(year), ...getSbgHolidays(year + 1)]
        .map((holiday) => format(holiday, 'yyyy-MM-dd'))
    );
    sbgHolidayKeysCache.set(year, keys);
  }
  return keys;
};

/** True for an SBG company holiday (observed date), the days PTO and calendar imports never count. */
export const isSbgHoliday = (date: Date) => (
  getSbgHolidayKeysNear(date.getFullYear()).has(format(date, 'yyyy-MM-dd'))
);

export const formatUsageNumber = (value: number) => {
  const rounded = round(value, 1);
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

/**
 * Annual balances and rollover jobs use the Eastern calendar. Keeping the UI
 * on that same date key prevents a viewer in another timezone from briefly
 * seeing a different year or a future booking as already used.
 */
export const getEasternDateKey = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}`;
};

export const getYearProgress = (todayYmd = getEasternDateKey()): YearProgress => {
  const parsed = parseISO(todayYmd);
  const year = parsed.getFullYear();
  const dayOfYear = getDayOfYear(parsed);
  const daysInYear = getDaysInYear(parsed);
  const elapsedFraction = daysInYear > 0 ? dayOfYear / daysInYear : 0;

  return {
    year,
    dayOfYear,
    daysInYear,
    elapsedFraction,
    percentComplete: Math.round(elapsedFraction * 100),
  };
};

/**
 * Tenure-managed first-year allotments are prorated from the employee's start
 * date. Their pace window therefore starts on that date, not January 1.
 */
export const getEmployeeElapsedFraction = (
  year: number,
  todayYmd: string,
  employeeStartDate?: string | null
) => {
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const startKey = employeeStartDate && isValidDateKey(toDateKey(employeeStartDate))
    ? toDateKey(employeeStartDate)
    : yearStart;
  const anchor = startKey > yearStart ? startKey : yearStart;
  const asOf = todayYmd < yearEnd ? todayYmd : yearEnd;

  if (anchor > yearEnd || asOf < anchor) return 0;

  const elapsedDays = differenceInCalendarDays(parseISO(asOf), parseISO(anchor)) + 1;
  const eligibleDays = differenceInCalendarDays(parseISO(yearEnd), parseISO(anchor)) + 1;
  return eligibleDays > 0 ? Math.min(Math.max(elapsedDays / eligibleDays, 0), 1) : 0;
};

const getBusinessDayKeys = (startYmd: string, endYmd: string) => {
  if (!isValidDateKey(startYmd) || !isValidDateKey(endYmd) || endYmd < startYmd) return [];
  const days = eachDayOfInterval({ start: parseISO(startYmd), end: parseISO(endYmd) })
    .filter((date) => !isWeekend(date) && !isSbgHoliday(date))
    .map((date) => format(date, 'yyyy-MM-dd'));
  // Calendar imports can occasionally describe weekend-only events. Their
  // stored hours still need a deterministic used/scheduled classification.
  return days.length > 0 ? days : [startYmd];
};

const getDailyAllocations = (entry: TimeOffUsageEntry) => {
  const hours = Math.max(Number(entry.hours) || 0, 0);
  if (hours <= 0) return [];
  const startYmd = toDateKey(entry.startDate);
  const endYmd = toDateKey(entry.endDate || entry.startDate);
  const businessDays = getBusinessDayKeys(startYmd, endYmd);
  if (businessDays.length === 0) return [];
  const hoursPerDay = hours / businessDays.length;
  return businessDays.map((day) => ({ day, hours: hoursPerDay }));
};

const splitApprovedHours = (entry: TimeOffUsageEntry, todayYmd: string) => {
  const hours = Number(entry.hours) || 0;
  const startYmd = toDateKey(entry.startDate);
  const endYmd = toDateKey(entry.endDate || entry.startDate);

  if (startYmd > todayYmd) return { used: 0, scheduled: hours };
  if (!endYmd || endYmd <= todayYmd) return { used: hours, scheduled: 0 };

  const businessDays = getBusinessDayKeys(startYmd, endYmd);
  if (businessDays.length === 0) return { used: hours, scheduled: 0 };
  const elapsedDays = businessDays.filter((day) => day <= todayYmd).length;
  const used = hours * (elapsedDays / businessDays.length);
  return { used: round(used), scheduled: round(hours - used) };
};

/**
 * Outlook IDs are intended to identify one calendar event, but the database
 * only has a non-unique index and historical syncs can leave duplicates. Keep
 * the newest copy so those rows cannot inflate employee totals.
 */
export const dedupeTimeOffEntries = <T extends TimeOffUsageEntry>(entries: T[]): T[] => {
  const withoutOutlookId: T[] = [];
  const byOutlookId = new Map<string, T>();

  entries.forEach((entry) => {
    if (!entry.outlookEventId) {
      withoutOutlookId.push(entry);
      return;
    }

    const current = byOutlookId.get(entry.outlookEventId);
    if (!current) {
      byOutlookId.set(entry.outlookEventId, entry);
      return;
    }

    // Match backend/src/scripts/dedupeOutlookEvents.ts: the most recently
    // created row is canonical even if an older duplicate was edited later, and
    // its stable sort keeps the first row when two timestamps are equal.
    if (toTimestamp(entry.createdAt) > toTimestamp(current.createdAt)) {
      byOutlookId.set(entry.outlookEventId, entry);
    }
  });

  return [...withoutOutlookId, ...Array.from(byOutlookId.values())];
};

export const aggregateTimeOffEntries = (
  rawEntries: TimeOffUsageEntry[],
  year: number,
  todayYmd = getEasternDateKey()
): TimeOffUsageSummary => {
  const summary: TimeOffUsageSummary = {
    pto: EMPTY_BUCKET(),
    sick: EMPTY_BUCKET(),
    wfh: EMPTY_BUCKET(),
    combined: EMPTY_BUCKET(),
  };
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;

  dedupeTimeOffEntries(rawEntries).forEach((entry) => {
    const startYmd = toDateKey(entry.startDate);
    const kind = entry.type === 'PTO' ? 'pto' : entry.type === 'SICK' ? 'sick' : entry.type === 'WFH' ? 'wfh' : null;
    if (!kind) return;

    const hours = Math.max(Number(entry.hours) || 0, 0);

    // WFH is a weekly schedule policy, not an annual start-date balance. Clip
    // it by each actual work date so Dec→Jan events land in the correct year.
    if (kind === 'wfh') {
      getDailyAllocations({ ...entry, hours })
        .filter(({ day }) => day >= yearStart && day <= yearEnd)
        .forEach(({ day, hours: allocatedHours }) => {
          if (entry.status === 'PENDING') summary.wfh.pending += allocatedHours;
          if (entry.status === 'APPROVED') {
            if (day <= todayYmd) summary.wfh.used += allocatedHours;
            else summary.wfh.scheduled += allocatedHours;
          }
        });
      return;
    }

    // PTO and sick follow the backend's annual start-date balance convention.
    if (startYmd < yearStart || startYmd > yearEnd) return;
    if (entry.status === 'PENDING') {
      summary[kind].pending += hours;
    } else if (entry.status === 'APPROVED') {
      const split = splitApprovedHours({ ...entry, hours }, todayYmd);
      summary[kind].used += split.used;
      summary[kind].scheduled += split.scheduled;
    }
  });

  (['used', 'scheduled', 'pending'] as const).forEach((bucket) => {
    summary.pto[bucket] = round(summary.pto[bucket]);
    summary.sick[bucket] = round(summary.sick[bucket]);
    summary.wfh[bucket] = round(summary.wfh[bucket]);
    summary.combined[bucket] = round(summary.pto[bucket] + summary.sick[bucket]);
  });

  return summary;
};

export const buildEmployeeUsageMap = <T extends TimeOffUsageEntry>(
  entries: T[],
  year: number,
  todayYmd = getEasternDateKey()
) => {
  const grouped = new Map<string, T[]>();

  entries.forEach((entry) => {
    const employeeId = typeof entry.employee === 'string'
      ? entry.employee
      : entry.employee?._id || entry.employee?.id;
    if (!employeeId) return;
    const current = grouped.get(employeeId) || [];
    current.push(entry);
    grouped.set(employeeId, current);
  });

  const usage = new Map<string, TimeOffUsageSummary>();
  grouped.forEach((employeeEntries, employeeId) => {
    usage.set(employeeId, aggregateTimeOffEntries(employeeEntries, year, todayYmd));
  });
  return usage;
};

export const calculateAllocationPace = (
  usage: UsageBucket,
  rawAllotment: number,
  elapsedFraction: number
): AllocationPace => {
  const allotment = Math.max(Number(rawAllotment) || 0, 0);
  const used = round(Math.max(usage.used, 0));
  const scheduled = round(Math.max(usage.scheduled, 0));
  const pending = round(Math.max(usage.pending, 0));
  const committed = round(used + scheduled);
  const potential = round(committed + pending);
  const available = round(allotment - committed);
  const straightLine = elapsedFraction > 0 ? used / elapsedFraction : used;
  // A forecast cannot be lower than time that is already approved, so the
  // straight-line estimate is rounded before the floor is applied. Rounding the
  // clamped value instead can land under a fractional committed total.
  const projected = Math.max(round(straightLine, 1), committed);

  let status: PaceStatus = 'within-pace';
  if (allotment === 0) {
    status = committed > 0 ? 'over-allotment' : pending > 0 ? 'pending-risk' : 'no-allotment';
  } else if (committed > allotment) {
    status = 'over-allotment';
  } else if (projected > allotment) {
    status = 'over-pace';
  } else if (potential > allotment) {
    status = 'pending-risk';
  }

  return {
    allotment,
    used,
    scheduled,
    pending,
    committed,
    potential,
    available,
    projected,
    usedPercent: allotment > 0 ? (used / allotment) * 100 : 0,
    scheduledPercent: allotment > 0 ? (scheduled / allotment) * 100 : 0,
    pendingPercent: allotment > 0 ? (pending / allotment) * 100 : 0,
    elapsedPercent: Math.min(Math.max(elapsedFraction * 100, 0), 100),
    status,
  };
};

export const combineAllocationPaces = (
  first: AllocationPace,
  second: AllocationPace
): AllocationPace => {
  const allotment = round(first.allotment + second.allotment);
  const used = round(first.used + second.used);
  const scheduled = round(first.scheduled + second.scheduled);
  const pending = round(first.pending + second.pending);
  const committed = round(used + scheduled);
  const potential = round(committed + pending);
  const available = round(allotment - committed);
  // Sum the category forecasts after each has applied its own approved-hours
  // floor. Re-projecting aggregated usage can let heavy scheduled PTO mask
  // expected future sick usage (or vice versa).
  const projected = round(first.projected + second.projected, 1);

  let status: PaceStatus = 'within-pace';
  if (allotment === 0) {
    status = committed > 0 ? 'over-allotment' : pending > 0 ? 'pending-risk' : 'no-allotment';
  } else if (committed > allotment) {
    status = 'over-allotment';
  } else if (projected > allotment) {
    status = 'over-pace';
  } else if (potential > allotment) {
    status = 'pending-risk';
  }

  return {
    allotment,
    used,
    scheduled,
    pending,
    committed,
    potential,
    available,
    projected,
    usedPercent: allotment > 0 ? (used / allotment) * 100 : 0,
    scheduledPercent: allotment > 0 ? (scheduled / allotment) * 100 : 0,
    pendingPercent: allotment > 0 ? (pending / allotment) * 100 : 0,
    elapsedPercent: Math.max(first.elapsedPercent, second.elapsedPercent),
    status,
  };
};

/** Canonical hybrid policy is two WFH days per week; 104 is legacy annual data. */
export const normalizeWeeklyWfhAllowance = (wfhEligible: boolean, rawAllotment: number) => {
  if (!wfhEligible) return 0;
  const value = Number(rawAllotment) || 0;
  return value > 0 && value <= 5 ? value : 2;
};

export const calculateWfhWeeklyPlan = (
  rawEntries: TimeOffUsageEntry[],
  year: number,
  weeklyAllowance: number
): WfhWeeklyPlan => {
  const weeklyDays = new Map<string, number>();
  const selectedYearWeeks = new Set<string>();
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;

  if (weeklyAllowance <= 0) {
    return { weeklyAllowance: 0, weeksWithUsage: 0, weeksOverAllowance: 0, highestWeekDays: 0 };
  }

  dedupeTimeOffEntries(rawEntries).forEach((entry) => {
    if (
      entry.type !== 'WFH' ||
      entry.status !== 'APPROVED' ||
      (Number(entry.hours) || 0) <= 0
    ) return;

    getDailyAllocations(entry)
      // Approved future dates are commitments too. Including them lets the
      // weekly-plan warning catch an overage before the scheduled day arrives.
      .forEach(({ day, hours }) => {
        const weekKey = format(startOfISOWeek(parseISO(day)), 'yyyy-MM-dd');
        weeklyDays.set(weekKey, (weeklyDays.get(weekKey) || 0) + (hours / 8));
        // A Monday-Sunday policy week is indivisible at New Year. Seed the
        // report with weeks that contain selected-year usage, but retain the
        // full total from adjacent-year days in those same weeks.
        if (day >= yearStart && day <= yearEnd) {
          selectedYearWeeks.add(weekKey);
        }
      });
  });

  const values = Array.from(selectedYearWeeks).map((weekKey) => round(weeklyDays.get(weekKey) || 0, 1));
  return {
    weeklyAllowance,
    weeksWithUsage: values.length,
    weeksOverAllowance: values.filter((value) => value > weeklyAllowance + 0.01).length,
    highestWeekDays: values.length > 0 ? Math.max(...values) : 0,
  };
};
